'use client';

import { faAngleDown } from '@fortawesome/pro-light-svg-icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Icon } from '@/components/ui/icon.component';
import { Link } from '@/components/ui/link/link.component';
import { shouldHideNav } from '@/utils/navbar.util';
import { HeaderNavItem } from '@/app/(main)/_components/sign-in-modal/_types/header.types';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

const HOVER_OPEN_DELAY_MS = 100;

export function HeaderNavbarItem({
  normalLabel,
  responsivenessLabel,
  url,
  subItems,
  id,
  target,
  dataTestId,
  dropdownDataTestId,
}: HeaderNavItem) {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const itemRef = useRef<HTMLDivElement>(null);

  const cancelScheduledOpen = useCallback(() => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = undefined;
    }
  }, []);

  useEffect(() => cancelScheduledOpen, [cancelScheduledOpen]);

  const handleMouseEnter = () => {
    cancelScheduledOpen();
    openTimeoutRef.current = setTimeout(() => setOpen(true), HOVER_OPEN_DELAY_MS);
  };

  const handleMouseLeave = () => {
    cancelScheduledOpen();
    setOpen(false);
  };

  if (shouldHideNav(pathname)) {
    return null;
  }

  return (
    <>
      {url && (
        <a
          className="content-center cursor-pointer"
          href={url}
          data-testid={`${id}-navlink`}
          target={target}
        >
          <div>
            <span className="xl:hidden">{responsivenessLabel}</span>
            <span className="hidden xl:inline">{normalLabel}</span>
          </div>
        </a>
      )}
      {!url && (
        <div
          ref={itemRef}
          className="h-full content-center relative"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
            <DropdownMenuTrigger
              asChild
              onPointerDown={(event) => {
                if (open && event.pointerType === 'mouse') {
                  event.preventDefault();
                }
              }}
              onClick={(event) => event.stopPropagation()}
              className="
                group
                border-y-2 border-transparent
                text-gray-700
                hover:text-green-500
                hover:border-b-green-500
                data-[state=open]:text-green-500
                data-[state=open]:border-b-green-500
                content-center
                cursor-pointer
                transition-all
                h-full
                px-4 xl:px-6
              "
              data-testid={dataTestId}
            >
              <button className="flex items-center gap-2 whitespace-nowrap">
                <span className="xl:hidden">{responsivenessLabel}</span>
                <span className="hidden xl:inline">{normalLabel}</span>
                <Icon
                  icon={faAngleDown}
                  className="hidden xl:block w-5 h-4 shrink-0 transition-transform duration-200 group-hover:rotate-180 group-data-[state=open]:rotate-180"
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              onPointerDownOutside={(event) => {
                if (itemRef.current?.contains(event.target as Node)) {
                  event.preventDefault();
                }
              }}
              className="p-0 border-0 rounded-b-xxl min-w-[var(--radix-dropdown-menu-trigger-width)] data-[state=open]:animate-in data-[state=closed]:animate-out"
              data-testid={dropdownDataTestId}
            >
              {subItems.map((subItem) =>
                subItem.onClick
                  ? (
                      <DropdownMenuItem
                        key={subItem.id}
                        data-testid={subItem.dataTestId}
                        data-analytics-event={AnalyticsEnum.CLICK_HEADER_NAV_ITEM}
                        data-analytics-properties={subItem['data-analytics-properties']}
                        className="cursor-pointer px-4 xl:px-6 py-4 text-gray-700 text-base whitespace-nowrap rounded-none hover:bg-gray-100 hover:text-gray-700 data-[highlighted]:bg-gray-100"
                        onClick={subItem.onClick}
                      >
                        {subItem.label}
                      </DropdownMenuItem>
                    )
                  : (
                      <DropdownMenuItem
                        key={subItem.id}
                        asChild
                        className="cursor-pointer px-4 xl:px-6 py-4 text-gray-700 text-base whitespace-nowrap rounded-none hover:bg-gray-100 hover:text-gray-700 data-[highlighted]:bg-gray-100"
                      >
                        <Link
                          prefetch={false}
                          href={subItem.url ?? '/'}
                          target={subItem.target}
                          data-testid={subItem.dataTestId}
                          data-analytics-event={AnalyticsEnum.CLICK_HEADER_NAV_ITEM}
                          data-analytics-properties={subItem['data-analytics-properties']}
                        >
                          {subItem.label}
                        </Link>
                      </DropdownMenuItem>
                    ),
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </>
  );
}
